import { useMemo } from "react";
import { BidViz } from "../types";
import { ChartData } from "./useBidsChartData";
import { format } from "date-fns";

export interface BidsChartOptionsParams {
  chartData: ChartData;
  bids: BidViz[];
}

export const useBidsChartOptions = ({ chartData, bids }: BidsChartOptionsParams): ApexCharts.ApexOptions => {
  const tooltip = useMemo(
    () => ({
      shared: false,
      intersect: true,
      custom: ({ series, dataPointIndex, w }: any) => {
        const label = w.config.xaxis.categories[dataPointIndex] as string;
        const [product, date] = label.split(" – ");
        const offered = series[0][dataPointIndex];
        const accepted = series[1][dataPointIndex];

        const filteredBids = bids.filter((b) => b.product_name === product && b.delivery_date === date);
        const sumPrice = filteredBids.reduce((sum, b) => sum + (b.accepted_price ?? 0), 0);
        const average = sumPrice / (filteredBids.length || 0) / 4;
        const formattedDate = format(new Date(date), "dd/MM/yyyy");

        return `
          <div style="padding:8px; font-size:12px; line-height:1.4;">
            <div style="font-weight:700; margin-bottom:6px;">
              ${formattedDate}
            </div>
            <div>${product}</div>
            <br />
            <div style="margin-bottom:6px; font-style:italic;">
              <div>Offered Quantity: ${offered}</div>
              <div>Accepted Quantity: ${accepted}</div>
            </div>
            <div style="margin-top:4px; font-style:italic;">
              Σ Accepted Price: €${sumPrice.toFixed(2)}
            </div>
            <div style="margin-top:4px; font-style:italic;">
              Average Accepted Price per hour: €${average.toFixed(2)}
            </div>
          </div>
        `;
      },
    }),
    [bids],
  );

  return useMemo(
    () => ({
      chart: { type: "bar", stacked: false },
      plotOptions: { bar: { horizontal: false, columnWidth: "50%" } },
      dataLabels: { enabled: false },
      stroke: { show: true, width: 1, colors: ["#fff"] },
      xaxis: {
        categories: chartData.categories,
        labels: {
          rotate: -90,
          trim: false,
          style: { fontSize: "8px", fontWeight: 700 },
          formatter: (val: string) => val.split(" – ")[0],
        },
        group: {
          style: { fontSize: "16px", fontWeight: 700 },
          groups: chartData.dateGroups,
        },
      },
      yaxis: { title: { text: "Quantity" } },
      legend: { position: "top" },
      grid: {
        column: {
          colors: ["#fcfefe", "#e6f7fc", "#cceff9", "#b3e8f6", "#99e0f3", "#80d9f0"],
        },
      },
      tooltip,
    }),
    [chartData.categories, chartData.dateGroups, tooltip],
  );
};
